import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { consoleAppConfig } from '@/console/app/config/console-app-config';
import { AuthenticatedRoute } from '@/console/app/protected-route/authenticated-route';
import { deviceRoutes } from '@/console/app/routes/devices/routes';
import { documentsRoutes } from '@/console/app/routes/documents/routes';
import { gatewayRoutes } from '@/console/app/routes/gateways/routes';
import { generalSettingsRoutes } from '@/console/app/routes/general-settings/routes';
import { groupsRoute } from '@/console/app/routes/groups/routes';
import { homeRoutes } from '@/console/app/routes/home/routes';
import { ideaasRoute } from '@/console/app/routes/ideaas/routes';
import { ideaasHubRoutes } from '@/console/app/routes/ideaas-hub/routes';
import { licensesRoutes } from '@/console/app/routes/licenses/routes';
import { mapRoutes } from '@/console/app/routes/map/routes';
import { notificationsRoutes } from '@/console/app/routes/notifications/routes';
import { onboardingRoutes } from '@/console/app/routes/onboarding/routes';
import { pipelinesRoutes } from '@/console/app/routes/pipelines/routes';
import { projectRoutes } from '@/console/app/routes/projects/routes';
import { rolesRoutes } from '@/console/app/routes/roles/routes';
import { AppRoot, rootLoader } from '@/console/app/routes/root/root';
import { securityHubRoutes } from '@/console/app/routes/security-hub/routes';
import { sraRoutesConfig } from '@/console/app/routes/sra/routes';
import { userProfileRoutes } from '@/console/app/routes/user-profile/routes';
import { userRoutes } from '@/console/app/routes/users/routes';
import { vscodeRoute } from '@/console/app/routes/vscode/routes';
import { ConsoleSidebar } from '@/console/features/console-sidebar/console-sidebar';
import { ConsolePermissionsProvider } from '@/console/features/permissions/console-permissions-provider';
import { authRoutes } from '@/shared/app/auth/routes';
import { errorRoutes } from '@/shared/app/routes/error/routes';
import { ErrorBoundaryRoute } from '@/shared/app/routes/error-boundary/error-boundary';
import { FullscreenLoader } from '@/shared/components/ui/fullscreen-loader/fullscreen-loader';
import { AppConfigProvider } from '@/shared/features/app-config/context/app-config-context';
import { useAssetsAppSyncEvents } from '@/shared/hooks/use-assets-app-sync-events/use-assets-app-sync-events';
import { useJobsAppSyncEvents } from '@/shared/hooks/use-jobs-app-sync-events/use-jobs-app-sync-events';
import { useLogRocket } from '@/shared/hooks/use-log-rocket/use-log-rocket';
import { TCustomRouteObject } from '@/shared/types/router.types';

const errorElement = (
  <AppConfigProvider value={consoleAppConfig}>
    <ConsolePermissionsProvider>
      <ErrorBoundaryRoute sidebar={<ConsoleSidebar />} />
    </ConsolePermissionsProvider>
  </AppConfigProvider>
);

const route: TCustomRouteObject[] = [
  authRoutes,
  onboardingRoutes,
  ideaasRoute,
  vscodeRoute,
  {
    path: '/',
    element: (
      <AppConfigProvider value={consoleAppConfig}>
        <ConsolePermissionsProvider>
          <AuthenticatedRoute>
            <AppRoot />
          </AuthenticatedRoute>
        </ConsolePermissionsProvider>
      </AppConfigProvider>
    ),
    loader: rootLoader,
    shouldRevalidate: () => false,
    hydrateFallbackElement: <FullscreenLoader />,
    errorElement,
    children: [
      homeRoutes,
      projectRoutes,
      deviceRoutes,
      gatewayRoutes,
      mapRoutes,
      licensesRoutes,
      groupsRoute,
      documentsRoutes,
      userRoutes,
      rolesRoutes,
      ideaasHubRoutes,
      securityHubRoutes,
      errorRoutes,
      pipelinesRoutes,
      userProfileRoutes,
      notificationsRoutes,
      generalSettingsRoutes,
      sraRoutesConfig,
    ],
  },
  {
    path: '/404',
    element: errorElement,
  },
  {
    path: '*',
    element: (
      <AppConfigProvider value={consoleAppConfig}>
        <AuthenticatedRoute>
          <Navigate to="/" replace />
        </AuthenticatedRoute>
      </AppConfigProvider>
    ),
  },
];

export const createAppRouter = () => createBrowserRouter(route);

export const AppRouter = () => {
  const router = createAppRouter();
  useLogRocket();
  useAssetsAppSyncEvents();
  useJobsAppSyncEvents();

  return <RouterProvider router={router} />;
};
